/**
 * Carrier invoice reconciliation API.
 *
 * Wraps `/api/v1/benefits/*`. The flow:
 *   uploadInvoice() → listInvoices() → getInvoiceReport(id) →
 *     drafts a correction letter via generateLetter() → markResolved().
 */
import client from "./client";

export type InvoiceStatus =
  | "uploaded"
  | "reconciling"
  | "reconciled"
  | "discrepancies_found";

export type DiscrepancyCode =
  | "ghost"
  | "missing"
  | "tier_mismatch"
  | "rate_mismatch"
  | "unknown"
  | null;

export interface CarrierInvoice {
  id: string;
  carrier_name: string;
  invoice_number: string | null;
  invoice_date: string;
  invoice_total: number;
  expected_total: number;
  discrepancy_total: number;
  line_count: number;
  matched_count: number;
  unmatched_count: number;
  discrepancy_count: number;
  status: InvoiceStatus;
  file_name: string | null;
  file_url: string | null;
  reconciled_at: string | null;
  resolved_at: string | null;
  created_at: string | null;
  warning?: string;
}

export interface CarrierInvoiceLine {
  id: string;
  employee_name: string | null;
  employee_identifier: string | null;
  matched_employee_id: string | null;
  plan_name: string | null;
  tier: string | null;
  premium_amount: number;
  expected_premium: number;
  overbill_amount: number;
  match_status: "matched" | "discrepancy" | "unmatched";
  discrepancy_code: DiscrepancyCode;
  discrepancy_detail: string | null;
}

export interface CarrierInvoiceReport extends CarrierInvoice {
  lines: CarrierInvoiceLine[];
}

export interface UploadInvoicePayload {
  carrier_name: string;
  invoice_date: string;
  invoice_number?: string;
  file_name?: string;
  csv_text?: string;
  pdf_text?: string;
  auto_reconcile?: boolean;
}

export async function uploadInvoice(
  payload: UploadInvoicePayload,
): Promise<CarrierInvoice> {
  const res = await client.post<CarrierInvoice>(
    "/benefits/invoices",
    payload,
  );
  return res.data;
}

export async function listInvoices(): Promise<CarrierInvoice[]> {
  const res = await client.get<CarrierInvoice[]>("/benefits/invoices");
  return res.data;
}

export async function getInvoiceReport(
  invoiceId: string,
): Promise<CarrierInvoiceReport> {
  const res = await client.get<CarrierInvoiceReport>(
    `/benefits/invoices/${invoiceId}`,
  );
  return res.data;
}

export async function reconcileInvoice(
  invoiceId: string,
): Promise<CarrierInvoiceReport> {
  const res = await client.post<CarrierInvoiceReport>(
    `/benefits/invoices/${invoiceId}/reconcile`,
  );
  return res.data;
}

export async function generateLetter(
  invoiceId: string,
  payload: { company_name?: string; admin_name?: string } = {},
): Promise<{ text: string }> {
  const res = await client.post<{ text: string }>(
    `/benefits/invoices/${invoiceId}/letter`,
    payload,
  );
  return res.data;
}

export async function markInvoiceResolved(
  invoiceId: string,
): Promise<CarrierInvoice> {
  const res = await client.post<CarrierInvoice>(
    `/benefits/invoices/${invoiceId}/resolve`,
  );
  return res.data;
}

export interface ReconciliationSummary {
  open_discrepancies: {
    id: string;
    carrier_name: string;
    invoice_date: string;
    discrepancy_total: number;
    discrepancy_count: number;
  }[];
  ytd_savings_identified: number;
}

export async function getReconciliationSummary(): Promise<ReconciliationSummary> {
  const res = await client.get<ReconciliationSummary>(
    "/benefits/reconciliation/summary",
  );
  return res.data;
}
